import { authRequest } from './client';
import {
  type ChannelDetailResponse,
  type ChannelPaymentDetailResponse,
  type ChannelSettlementDetailResponse,
  type ListChannelPaymentsParams,
  type ListChannelPaymentsResponse,
  type ListChannelSettlementsParams,
  type ListChannelSettlementsResponse,
  type ListChannelsParams,
  type ListChannelsResponse,
} from './types';

// ─── backoffice-api channels client (Task 14, feature-031 Phase B) ────────
//
// Thin fetch wrappers around backoffice-api's `/v1/channels`,
// `/v1/channel-payments`, and `/v1/channel-settlements` endpoints — every
// read requires CHANNELS_VIEWER (or SUPERADMIN); `settleNowChannel`/
// `retryChannelSettlement` require CHANNELS_MANAGER, enforced by the
// backend and mirrored client-side via `hasRole`-gated controls in the
// route components. Same "thin wrapper, own response types" convention as
// `api/invoices.ts`.

export function listChannels(params: ListChannelsParams = {}): Promise<ListChannelsResponse> {
  const query = new URLSearchParams();

  if (params.page !== undefined) query.set('page', String(params.page));
  if (params.pageSize !== undefined) query.set('pageSize', String(params.pageSize));
  if (params.status !== undefined) query.set('status', params.status);
  if (params.merchantId !== undefined) query.set('merchantId', params.merchantId);
  if (params.assetId !== undefined) query.set('assetId', params.assetId);
  if (params.search !== undefined) query.set('search', params.search);
  if (params.sortBy !== undefined) query.set('sortBy', params.sortBy);
  if (params.sortOrder !== undefined) query.set('sortOrder', params.sortOrder);

  const qs = query.toString();
  return authRequest<ListChannelsResponse>(`/channels${qs ? `?${qs}` : ''}`);
}

/** Uses the channel's DB `id` (the same `id` field on the list item) — NOT `publicId`. */
export function getChannel(channelId: string): Promise<ChannelDetailResponse> {
  return authRequest<ChannelDetailResponse>(`/channels/${channelId}`);
}

/**
 * Opens a settlement for this channel immediately, bypassing only the
 * platform's minimum settlement value — CHANNELS_MANAGER-only. Addressed by
 * `publicId` (the value an admin actually has in front of them), not the DB
 * `id` every read route above uses.
 */
export function settleNowChannel(publicId: string): Promise<ChannelSettlementDetailResponse> {
  return authRequest<ChannelSettlementDetailResponse>(`/channels/${publicId}/settle-now`, { method: 'POST' });
}

export function listChannelPayments(
  params: ListChannelPaymentsParams = {},
): Promise<ListChannelPaymentsResponse> {
  const query = new URLSearchParams();

  if (params.page !== undefined) query.set('page', String(params.page));
  if (params.pageSize !== undefined) query.set('pageSize', String(params.pageSize));
  if (params.status !== undefined) query.set('status', params.status);
  if (params.subStatus !== undefined) query.set('subStatus', params.subStatus);
  if (params.merchantId !== undefined) query.set('merchantId', params.merchantId);
  if (params.channelId !== undefined) query.set('channelId', params.channelId);
  if (params.settlementId !== undefined) query.set('settlementId', params.settlementId);
  if (params.assetId !== undefined) query.set('assetId', params.assetId);
  if (params.amountUsdMin !== undefined) query.set('amountUsdMin', params.amountUsdMin);
  if (params.amountUsdMax !== undefined) query.set('amountUsdMax', params.amountUsdMax);
  if (params.from !== undefined) query.set('from', params.from);
  if (params.to !== undefined) query.set('to', params.to);
  if (params.sortBy !== undefined) query.set('sortBy', params.sortBy);
  if (params.sortOrder !== undefined) query.set('sortOrder', params.sortOrder);

  const qs = query.toString();
  return authRequest<ListChannelPaymentsResponse>(`/channel-payments${qs ? `?${qs}` : ''}`);
}

export function getChannelPayment(channelPaymentId: string): Promise<ChannelPaymentDetailResponse> {
  return authRequest<ChannelPaymentDetailResponse>(`/channel-payments/${channelPaymentId}`);
}

export function listChannelSettlements(
  params: ListChannelSettlementsParams = {},
): Promise<ListChannelSettlementsResponse> {
  const query = new URLSearchParams();

  if (params.page !== undefined) query.set('page', String(params.page));
  if (params.pageSize !== undefined) query.set('pageSize', String(params.pageSize));
  if (params.status !== undefined) query.set('status', params.status);
  if (params.merchantId !== undefined) query.set('merchantId', params.merchantId);
  if (params.channelId !== undefined) query.set('channelId', params.channelId);
  if (params.sortBy !== undefined) query.set('sortBy', params.sortBy);
  if (params.sortOrder !== undefined) query.set('sortOrder', params.sortOrder);

  const qs = query.toString();
  return authRequest<ListChannelSettlementsResponse>(`/channel-settlements${qs ? `?${qs}` : ''}`);
}

export function getChannelSettlement(channelSettlementId: string): Promise<ChannelSettlementDetailResponse> {
  return authRequest<ChannelSettlementDetailResponse>(`/channel-settlements/${channelSettlementId}`);
}

/** Retries a FAILED settlement — CHANNELS_MANAGER-only. Addressed by `publicId`, same reasoning as `settleNowChannel`. */
export function retryChannelSettlement(publicId: string): Promise<ChannelSettlementDetailResponse> {
  return authRequest<ChannelSettlementDetailResponse>(`/channel-settlements/${publicId}/retry`, { method: 'POST' });
}
